import {
	NavLink,
	useLocation,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import { message, type MenuProps } from "antd";
import { useMutation } from "@apollo/client";
import { LockOutlined, LogoutOutlined } from "@ant-design/icons";
import { NAV_ITEMS, NavItem, ROUTES } from "@/consts";
import { filterNavItems, isNavGroup } from "@/services/auth/permissions";
import { useAuth } from "@/contexts";
import { ActionTypes } from "::root/src/contexts/AuthContext/types";
import { LOGOUT } from "@/components/UserAuth/mutations";

type MenuItem = Required<MenuProps>["items"][number];

const toMenuItems = (items: NavItem[]): MenuItem[] =>
	items.map((item) =>
		isNavGroup(item)
			? {
					key: item.key,
					label: item.label,
					children: toMenuItems(item.children),
			  }
			: { key: item.to, label: <NavLink to={item.to}>{item.label}</NavLink> },
	);

const dropdownItems: MenuItem[] = [
	{
		key: "change-password",
		icon: <LockOutlined />,
		label: "Change Password",
	},
	{
		type: "divider",
	},
	{
		key: "logout",
		icon: <LogoutOutlined />,
		label: "Logout",
		danger: true,
	},
];

export default function useSideBarLayout() {
	const navigate = useNavigate();
	const [logout, { loading: logoutLoading }] = useMutation(LOGOUT);
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const pathnameWithParams =
		searchParams.toString()?.length > 1
			? `${pathname}?${searchParams.toString()}`
			: pathname;

	const {
		state: { user },
		dispatch,
	} = useAuth();
	const items = toMenuItems(filterNavItems(NAV_ITEMS, user?.permissions ?? []));

	const expandedKeys = NAV_ITEMS.filter(isNavGroup)
		.filter((group) =>
			group.children.some((child) => child.to === pathnameWithParams),
		)
		.map((group) => group.key);

	const handleLogout: MenuProps["onClick"] = ({ key }) => {
		if (key === "logout") {
			logout()
				.then((res) => {
					if (res.data?.response?.statusCode === 200) {
						dispatch({ type: ActionTypes.LOGOUT });
					}
				})
				.catch((err) => {
					if (err.graphQLErrors[0].statusCode === 422) {
						const { errors } = err.graphQLErrors[0];
						message.error(Object.values(errors)[0] as string);
						return;
					}
					message.error(err?.message || "Something went wrong");
				});
		}
		if (key === "change-password") {
			navigate(ROUTES.ChangePassword);
		}
	};

	return {
		items,
		pathnameWithParams,
		dropdownItems,
		expandedKeys,
		handleLogout,
		logoutLoading,
	};
}
